import moment from 'moment';
import { useSelector } from 'react-redux';
import { DELIVERY_SALE_TYPES } from '../constants/saleType';
import determineLocationSessions from '../utils/ordering/determineLocationSessions';
import {
  getBufferLocation,
  getBufferSaleType,
  getFutureOrderingLimitBySaleType,
  getLocation,
  getOrderingWindowPadding,
} from '../selectors';

export function determineFutureTimeSelectorMinMax({
  saleType,

  // This is used to get the earliest and latest times:
  // If it's the initial pre screens (before it goes to menu or when the user picks a location)
  loadedLocation,
  bufferLocation,

  // to determine location sessions
  orderingWindowPadding,
  futureOrderingLimit,
  minuteInterval,
  selectedDateTime,
}: {
  saleType: SALE_TYPE;
  loadedLocation?: POSLocation;
  bufferLocation?: POSLocation;
  orderingWindowPadding: OrderingWindowPadding | null;
  futureOrderingLimit: number;
  minuteInterval: number;
  selectedDateTime?: string;
}) {
  const location = DELIVERY_SALE_TYPES.includes(saleType)
    ? null
    : loadedLocation || bufferLocation;

  const locationWithSessions = location
    ? {
        ...location,
        ...determineLocationSessions(
          location,
          orderingWindowPadding,
          saleType,
          futureOrderingLimit,
          minuteInterval,
          selectedDateTime,
        ),
      }
    : null;

  // Earliest set time
  const fallbackMinTime = moment(selectedDateTime).startOf('day');

  const minTimeForLoadedLocation = locationWithSessions
    ? moment(locationWithSessions.orderingWindowStart)
    : null;

  const minTimeForLoadedLocationIsBeforeCurrentTime =
    minTimeForLoadedLocation?.isBefore(moment()) || false;

  const nowWithPadding = moment().add(
    locationWithSessions?.averageOrderWaitTime || 0,
    'minutes',
  );

  const timeSelectorMinTime = minTimeForLoadedLocationIsBeforeCurrentTime
    ? nowWithPadding
    : minTimeForLoadedLocation || fallbackMinTime;

  // latest set time
  const fallbackMaxTime = moment(selectedDateTime).endOf('day');
  const timeSelectorMaxTime = locationWithSessions
    ? moment(locationWithSessions.orderingWindowEnd)
    : fallbackMaxTime;

  const timeNotInsideOrderingWindow = !moment(selectedDateTime).isBetween(
    timeSelectorMinTime,
    timeSelectorMaxTime,
  );

  return {
    timeSelectorMinTime,
    timeSelectorMaxTime,
    timeNotInsideOrderingWindow,
  };
}

/**
 * # useFutureTimeSelectorMinMaxBase
 *
 * When the user changes the date we need to generate new min and max times based on the buffer
 * location and location (if the user has gone passed details screens). It needs to also handle
 * scenarios where the user hasn't selected a buffer location in a buffer flow.
 *
 * @param selectedDateTime - the input value
 *
 * @returns [timeSelectorMinTime, timeSelectorMaxTime, timeNotInsideOrderingWindow]
 */
export default function useFutureTimeSelectorMinMaxBase({
  selectedDateTime,
  loadedSaleType,
  minuteInterval = 5,
}: {
  selectedDateTime?: string;
  loadedSaleType?: SALE_TYPE;
  minuteInterval?: number;
}) {
  const saleTypeFromBuffer = useSelector(getBufferSaleType);
  const saleType = loadedSaleType || saleTypeFromBuffer;

  const loadedLocation = useSelector(getLocation);
  const bufferLocation = useSelector(getBufferLocation);

  const orderingWindowPadding = useSelector(getOrderingWindowPadding);

  const futureOrderingLimit = useSelector(
    getFutureOrderingLimitBySaleType({
      saleType,
    }),
  );

  const {
    timeSelectorMinTime,
    timeSelectorMaxTime,
    timeNotInsideOrderingWindow,
  } = determineFutureTimeSelectorMinMax({
    saleType,
    loadedLocation,
    bufferLocation,
    orderingWindowPadding,
    futureOrderingLimit,
    minuteInterval,
    selectedDateTime,
  });

  return {
    timeSelectorMinTime,
    timeSelectorMaxTime,
    timeNotInsideOrderingWindow,
  };
}
